import { useMeetingManager } from 'amazon-chime-sdk-component-library-react'

export type VideoState = 'inactive' | 'active' | 'error'

type UseVideoStateProps = {
  isVideoEnabled: boolean
  videoError?: string
  selectedVideoInput?: string
}

/**
 * Determines the current state of the local video based on Chime SDK data.
 * Works for both preview mode (before joining meeting) and meeting mode (after joining).
 *
 * @param isVideoEnabled - Whether video is enabled
 * @param videoError - Optional error message from device selection
 * @param selectedVideoInput - Optional video input device ID (for preview mode)
 * @returns Video state: 'inactive' | 'active' | 'error'
 */
export function useVideoState({ isVideoEnabled, videoError }: UseVideoStateProps): VideoState {
  const meetingManager = useMeetingManager()

  // Check if we're in preview mode (not in meeting) or meeting mode
  const isInMeeting = !!meetingManager.meetingSession

  // Error state takes priority
  if (videoError) {
    return 'error'
  }

  // Video is disabled/off
  if (!isVideoEnabled) {
    return 'inactive'
  }

  // In preview mode (before joining meeting)
  if (!isInMeeting) {
    // If device is selected, assume it's active
    // The actual preview binding happens in PreviewVideo component
    // The component will handle the loading state internally
    return 'active'
  }

  // Video is streaming
  return 'active'
}
